import type { BilingualName, BilingualText, BilingualAddress } from './common';

export type TitleLegacy = 'Mr' | 'Mrs' | 'Ms' | 'Prof' | 'Dr';

export enum Title {
  Mr = 1,
  Ms = 2,
  Other = 3,
  Mrs = 4,
  Dr = 5,
  Prof = 6,
}

export enum ContactPreference {
  Personal = 1,
  Professional = 2,
}

export enum ProfessionalPath {
  ActiveMuseumProfessional = 1,
  Student = 2,
  InterestedNonProfessional = 3,
}

export enum Gender {
  Female = 1,
  Male = 2,
  Other = 3,
  PreferNotToSay = 4,
}
export type MembershipCategory =
  | 'Regular'
  | 'Retired Professional'
  | 'Associate'
  | 'Student'
  | 'Contributor'
  | 'Supporting';
export type CommunicationLanguage = 'English' | 'French' | 'Spanish';

export enum MembershipType {
  Regular = 1,
  Student = 2,
  Retired = 3,
  Honorary = 4,
}

export enum WorkSector {
  Museology = 1,
  Education = 2,
  Documentation = 3,
  Conservation = 4,
  Exhibition = 5,
  Management = 6,
  Communication = 7,
  Other = 8,
}

/** Vignette (physical item) delivery status. */
export enum VignetteStatus {
  NotSent = 0,
  SentByMail = 1,
  ReceivedInPerson = 2,
}

export interface MemberVignetteRecord {
  id: string;
  memberId: string;
  year: number;
  status: VignetteStatus;
  trackingNumber?: string | null;
  updatedAt: string;
  deliveredAt?: string | null;
}

export enum StudyDegreeType {
  Bachelors = 1,
  Masters = 2,
  PhD = 3,
  Other = 4,
}

export interface Study {
  id: string;
  titleEn: string;
  titleGr: string;
  degreeType: StudyDegreeType;
  /** @deprecated use institutionEn / institutionGr */
  institution?: string;
  institutionEn?: string;
  institutionGr?: string;
  isCurrent: boolean;
  startYear?: number;
  duration?: number;
  completionYear?: number;
}

/**
 * API shape for individual member: GET /members/me and PUT /members/me.
 * Nested bilingual types align with backend IndividualMemberDto.
 */
export interface IndividualMemberDto {
  id: string;
  title?: Title;
  name: BilingualName;
  residential?: BilingualAddress;
  gender?: Gender;
  dateOfBirth?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  email?: string;
  contactPreference?: ContactPreference;
  professionalPath?: ProfessionalPath;
  organisationName?: BilingualText;
  professionalTitle?: BilingualText;
  professionalInstitutionName?: BilingualText;
  professionalTitleDisplay?: string;
  professionalInstitutionNameDisplay?: string;
  workSector?: WorkSector;
  studies?: Study[];
  isGraduateStudent: boolean;
  isRetired: boolean;
  isVolunteer: boolean;
  isHonoraryMember: boolean;
  professionalAddress?: string;
  professionalAddressEn?: string;
  professionalAddressGr?: string;
  professionalPostalCode?: string;
  professionalCity?: string;
  professionalCityEn?: string;
  professionalCityGr?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  mainCommittee?: string;
  additionalCommittees?: string[];
  membershipType: MembershipType;
  registrationYear: number;
  /** Optional; included in match response and some admin views. */
  memberCardNumber?: string;
  /** National committee / ICOM the member transfers from. */
  transferFromIcom?: string;
  /** Previous membership card number when transferring. */
  previousCardNumber?: string;
  /** Vignette (physical item) delivery status. */
  vignetteStatus?: VignetteStatus;
  /** Tracking number when sent by mail. */
  vignetteTrackingNumber?: string;
  /** When vignette status was last updated. */
  vignetteUpdatedAt?: string;
  importProblems?: string;
  sourceRecordKey?: string;
  /** Whether the linked user account can log in (admin views). */
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Preferred language for display (matches UI language when passed from i18n).
 * When not provided, defaults to preferring EN then GR.
 */
export type DisplayLanguage = 'en' | 'gr';

/**
 * Display first name from DTO.
 * @param preferredLang - When provided, prefer that language; otherwise prefer EN then GR.
 */
export function getMemberDisplayFirstName(member: IndividualMemberDto, preferredLang?: DisplayLanguage): string {
  if (preferredLang === 'gr') {
    return member.name?.firstNameGr ?? member.name?.firstNameEn ?? '';
  }
  return member.name?.firstNameEn ?? member.name?.firstNameGr ?? '';
}

/**
 * Display last name from DTO.
 * @param preferredLang - When provided, prefer that language; otherwise prefer EN then GR.
 */
export function getMemberDisplayLastName(member: IndividualMemberDto, preferredLang?: DisplayLanguage): string {
  if (preferredLang === 'gr') {
    return member.name?.lastNameGr ?? member.name?.lastNameEn ?? '';
  }
  return member.name?.lastNameEn ?? member.name?.lastNameGr ?? '';
}

/**
 * View/display type only. Derive display fields from IndividualMemberDto; do not use for API request/response.
 * API returns IndividualMemberDto; use getMemberDisplayFirstName/getMemberDisplayLastName for name display.
 */
export interface IndividualMember {
  id: string;
  title?: Title;
  firstName: string;
  lastName: string;
  firstNameEn?: string;
  firstNameGr?: string;
  lastNameEn?: string;
  lastNameGr?: string;
  gender?: Gender;
  dateOfBirth?: string;
  residentialAddress?: string;
  residentialAddressEn?: string;
  residentialAddressGr?: string;
  residentialPostalCode?: string;
  residentialPostalCodeEn?: string;
  residentialPostalCodeGr?: string;
  residentialCity?: string;
  residentialCityEn?: string;
  residentialCityGr?: string;
  residentialCountry?: string;
  residentialCountryEn?: string;
  residentialCountryGr?: string;
  phone?: string;
  website?: string;
  email: string;
  contactPreference?: ContactPreference;
  professionalPath?: ProfessionalPath;
  organisationName?: BilingualText;
  professionalTitleEn?: string;
  professionalTitleGr?: string;
  workSector?: WorkSector;
  studies?: Study[];
  isGraduateStudent: boolean;
  isRetired: boolean;
  isVolunteer: boolean;
  isHonoraryMember: boolean;
  professionalInstitutionNameEn?: string;
  professionalInstitutionNameGr?: string;
  professionalAddress?: string;
  professionalPostalCode?: string;
  professionalCity?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  mainCommittee?: string;
  additionalCommittees?: string[];
  membershipType: MembershipType;
  registrationYear: number;
  createdAt: string;
  updatedAt: string;
}

export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Member - Unified display interface for admin/list views
 * 
 * IMPORTANT: This interface is a UNION TYPE that combines fields from both IndividualMember and Institution entities.
 * It is NOT a direct mapping to backend entities. Use this interface ONLY for:
 * - Admin member list views
 * - Search/filter operations
 * - Display purposes where you need a unified view of both member types
 * 
 * For specific operations:
 * - Use `IndividualMember` interface for individual member operations
 * - Use `Institution` interface for institution operations
 * 
 * Field Usage:
 * - For IndividualMembers: Use personal* fields (personalEmail, personalTel, etc.)
 * - For Institutions: Use institution* fields (institutionName, institutionEmail, etc.)
 * - The presence of institutionName indicates an Institution member
 * - The absence of institutionName (or presence of personalEmail) indicates an IndividualMember
 * 
 * Note: Not all fields will be populated for all member types. Check the role field
 * and presence of institutionName to determine which fields are applicable.
 */
export interface Member {
  id: string;
  // Personal Info (for IndividualMembers)
  title?: Title;
  firstName: string;
  lastName: string;
  gender?: Gender;
  dateOfBirth?: string; // ISO string

  // Institution Info (for Institutions - only populated when role is InstitutionMember)
  institutionName?: string;
  institutionWebsite?: string;
  institutionPosition?: string;
  institutionProfessional?: string;
  institutionCity?: string;
  institutionPostalCode?: string;
  institutionCountry?: string;
  institutionEmail?: string;
  institutionTel?: string;
  institutionFax?: string;
  institutionMainContact?: boolean;

  // Personal Contact Info (for IndividualMembers - only populated when role is IndividualMember)
  address?: string;
  personalCity?: string;
  personalPostalCode?: string;
  personalCountry?: string;
  personalEmail?: string;
  personalTel?: string;
  personalFax?: string;
  personalMainContact?: boolean;

  // Membership
  membershipCategory: MembershipCategory;
  /** Optional; used in list display when API provides it */
  membershipType?: MembershipType | string;
  /** Optional; member card number (e.g. from catalog import). */
  memberCardNumber?: string;
  /** Optional; used in list display when API provides it */
  workSector?: WorkSector | string;
  communicationLanguage: CommunicationLanguage;
  mainCommittee?: string;
  otherCommittees?: string[];

  // Authorizations
  publishData?: boolean;
  diffuseData?: boolean;

  // Declaration
  acceptedEthics: boolean;
  declarationDate?: string;

  // System fields
  email: string;
  role: 'member' | 'staff';
  avatar?: string;
  region?: string;
  museumType?: string;
  museumName?: string;
  position?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MemberFilters {
  region?: string;
  museumType?: string;
  role?: string;
  isActive?: boolean;
  search?: string;
}

export interface MemberListResponse {
  members: Member[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MemberSearchParams {
  page?: number;
  limit?: number;
  filters?: MemberFilters;
}

export interface Region {
  id: string;
  name: string;
  nameGr: string;
}

export interface MuseumType {
  id: string;
  name: string;
  nameGr: string;
}
